import {useContext, useEffect, useState} from 'react'

import PropTypes from 'prop-types'

import AtomButton, {
  atomButtonColors,
  atomButtonDesigns,
  atomButtonShapes,
  atomButtonSizes
} from '@s-ui/react-atom-button'
import AtomIcon, {ATOM_ICON_SIZES} from '@s-ui/react-atom-icon'
import AtomPopover, {atomPopoverPositions} from '@s-ui/react-atom-popover'
import AtomSwitch, {atomSwitchSizes} from '@s-ui/react-atom-switch'
import Context from '@s-ui/react-context'
import {useMount} from '@s-ui/react-hooks'
import MoleculeBadgeCounter, {moleculeBadgeCounterSizes} from '@s-ui/react-molecule-badge-counter'
import {useRouter} from '@s-ui/react-router'
import {useMediaQuery, useMediaQueryBreakpoints} from '@adv-mt/mt-layout-render-media-query'
import IconBellRing from '@adv-mt/mt-svg-icons/lib/BellRing.js'
import IconInfoOutline from '@adv-mt/mt-svg-icons/lib/InfoOutline.js'

import CardAlertNavigation from './navigation/index.js'
import OnboardingAlert from './onboarding/index.js'
import {BASE_CLASS} from './config.js'
import {getDisplayAlertName} from './getDisplayAlertName.js'
import {useTracking} from './useTracking.js'

const pushSwitchId = 'push-notifications-switch'
const emailSwitchId = 'email-notifications-switch'

const CardAlert = ({
  allowFiltersUpdate = true,
  listFiltersOptions,
  onRefreshAlert,
  pageName,
  setNotificationType,
  setShow,
  userAlert
}) => {
  const {domain, i18n, isMotos} = useContext(Context)
  const isMobile = domain.get('isMobile')
  const isHigherThanMobile = useMediaQuery(useMediaQueryBreakpoints.sm, {
    defaultMatches: !isMobile
  })
  const config = domain.get('config')
  const {DEFAULT_NOTIFICATIONS} = config.get('navigation')
  const {push} = useRouter()

  const {FREQUENCY_NOTIFICATION_DAILY, FREQUENCY_NOTIFICATION_REALTIME} = config.get('alerts')
  const {USER_ALERTS_MATCHES_PATH_PATTERN, ADS_LIST_SEARCH_PATH_PATTERN} = config.get('page')

  const {activeEmail, alertId, alertName, activePush, filters, frequencyNotification} = userAlert

  const displayAlertName = getDisplayAlertName({alertName, config, i18n})

  const FREQUENCY_LABELS = {
    [FREQUENCY_NOTIFICATION_REALTIME]: i18n.t('SEARCH_SAVE_FREQUENCY_SELECTION.REAL_TIME', 2).toLowerCase(),
    [FREQUENCY_NOTIFICATION_DAILY]: i18n.t('SEARCH_SAVE_FREQUENCY_SELECTION.DAILY', 2).toLowerCase()
  }

  const frequencyNotificationLabel = `${i18n.t('SEARCH_SAVE_NOTIFICATION_NEWS')} ${
    FREQUENCY_LABELS[frequencyNotification] ?? ''
  }`

  const {emailNotificationsAllowed, emailNotificationsDismissed, pushNotificationsAllowed, pushNotificationsDismissed} =
    useTracking()

  const [unseenSavedSearchMatchesById, setUnseenSavedSearchMatchesById] = useState(DEFAULT_NOTIFICATIONS)
  const [updatedAlert, setUpdatedAlert] = useState(userAlert)

  useEffect(() => {
    const updateChannelSavedSearch = async () => {
      await domain.get('update_channel_user_alert_use_case').execute({
        userAlert: updatedAlert,
        alertId: updatedAlert.alertId,
        activeEmail: updatedAlert.activeEmail,
        activePush: updatedAlert.activePush
      })
    }
    updatedAlert !== userAlert && updateChannelSavedSearch()
  }, [updatedAlert, userAlert, domain])

  const handleTogglePushNotifications = activePush => {
    setUpdatedAlert(prevState => ({
      ...prevState,
      activePush: !activePush
    }))
    activePush ? pushNotificationsDismissed() : pushNotificationsAllowed()
  }

  const handleToggleEmailNotifications = activeEmail => {
    setUpdatedAlert(prevState => ({
      ...prevState,
      activeEmail: !activeEmail
    }))
    activeEmail ? emailNotificationsDismissed() : emailNotificationsAllowed()
  }

  const resetUnseenSavedSearchMatchesById = async alertId => {
    await domain.get('reset_unseen_saved_search_matches_by_id_use_case').execute({
      alertId
    })
  }

  const handleRedirectTo = url => push(url)

  const handleRedirectToAlertsMatchesPage = () => {
    push(USER_ALERTS_MATCHES_PATH_PATTERN.replace(':alertId', alertId))
  }

  const handleRedirectToFiltersSearch = async () => {
    const [error, response] = await domain.get('get_url_params_search_use_case').execute({
      search: filters
    })
    if (error) return handleRedirectTo(ADS_LIST_SEARCH_PATH_PATTERN)
    resetUnseenSavedSearchMatchesById(alertId)
    handleRedirectTo(response.url)
  }

  const handleClickCTAButton = () => {
    isMotos ? handleRedirectToFiltersSearch() : handleRedirectToAlertsMatchesPage()
  }

  const getUnseenSavedSearchMatchesById = async alertId => {
    const [, unseenSavedSearchMatchesById] = await domain
      .get('get_unseen_saved_search_matches_use_case')
      .execute(alertId)

    setUnseenSavedSearchMatchesById(unseenSavedSearchMatchesById.counter)
  }

  useMount(() => {
    getUnseenSavedSearchMatchesById(alertId)
  })

  const ctaButton = (
    <AtomButton
      design={atomButtonDesigns.OUTLINE}
      className={`${BASE_CLASS}-button`}
      color={atomButtonColors.PRIMARY}
      fullWidth
      onClick={handleClickCTAButton}
      size={isHigherThanMobile ? atomButtonSizes.MEDIUM : atomButtonSizes.LARGE}
      shape={atomButtonShapes.CIRCULAR}
    >
      {i18n.t('SEARCH_SAVE_SHOW_RESULTS')}
    </AtomButton>
  )

  return (
    <div className={`${BASE_CLASS}-alertbox`}>
      <div className={`${BASE_CLASS}-header`}>
        <div className={`${BASE_CLASS}-header--container`}>
          {unseenSavedSearchMatchesById > 0 ? (
            <div className={`${BASE_CLASS}-header--bellContainer`}>
              <MoleculeBadgeCounter size={moleculeBadgeCounterSizes.SMALL}>
                <div className={`${BASE_CLASS}-header--bellCounter`}>
                  {<IconBellRing size={ATOM_ICON_SIZES.medium} />}
                </div>
              </MoleculeBadgeCounter>
            </div>
          ) : (
            <div className={`${BASE_CLASS}-header---bell`}>{<IconBellRing size={ATOM_ICON_SIZES.medium} />}</div>
          )}
          <h4 className={`${BASE_CLASS}-header--title`}>{displayAlertName}</h4>
        </div>
        <CardAlertNavigation
          allowFiltersUpdate={allowFiltersUpdate}
          filtersProps={listFiltersOptions}
          handleClickCTAButton={handleClickCTAButton}
          onRefreshAlert={onRefreshAlert}
          pageName={pageName}
          setNotificationType={setNotificationType}
          setShow={setShow}
          userAlert={updatedAlert}
        />
      </div>
      <div className={`${BASE_CLASS}-status`}>
        <div className={`${BASE_CLASS}-status--group`}>
          <AtomPopover
            content={
              <div className={`${BASE_CLASS}-status--popover`}>{i18n.t('SEARCH_SAVE_POPOVER_PUSH_NOTIFICATIONS')}</div>
            }
            hideArrow={false}
            placement={atomPopoverPositions.BOTTOM}
            type="dark"
          >
            <span>
              <AtomIcon size={ATOM_ICON_SIZES.small}>
                <IconInfoOutline />
              </AtomIcon>
            </span>
          </AtomPopover>
          <h4 className={`${BASE_CLASS}-status--title`}>{frequencyNotificationLabel}</h4>
        </div>
        <div className={`${BASE_CLASS}-status--channel`}>
          <AtomSwitch
            id={pushSwitchId}
            checked={updatedAlert.activePush}
            fullWidth={!isHigherThanMobile}
            labelLeft={i18n.t('SEARCH_SAVE_NOTIFICATION_TYPES.MOBILE')}
            onToggle={() => handleTogglePushNotifications(updatedAlert.activePush)}
            value={activePush}
            size={atomSwitchSizes.LARGE}
            isFitted
          />
          <AtomSwitch
            id={emailSwitchId}
            checked={updatedAlert.activeEmail}
            fullWidth={!isHigherThanMobile}
            labelLeft={i18n.t('SEARCH_SAVE_NOTIFICATION_TYPES.EMAIL')}
            onToggle={() => handleToggleEmailNotifications(updatedAlert.activeEmail)}
            value={activeEmail}
            size={atomSwitchSizes.LARGE}
            isFitted
          />
          {isHigherThanMobile ? ctaButton : null}
        </div>
      </div>
      {!isHigherThanMobile ? ctaButton : null}
    </div>
  )
}

CardAlert.displayName = 'CardAlert'
CardAlert.propTypes = {
  allowFiltersUpdate: PropTypes.bool,
  listFiltersOptions: PropTypes.object,
  onRefreshAlert: PropTypes.func,
  pageName: PropTypes.string,
  setNotificationType: PropTypes.func,
  setShow: PropTypes.func,
  userAlert: PropTypes.object
}

export default CardAlert
export {OnboardingAlert}
